// Care-task pillar pages. These sit at /guides/<task> alongside the grass-type
// guides and act as topic hubs covering fertilizing, weed control, watering,
// mowing — the four lawn-care tasks that span every grass type.
//
// Each pillar is intentionally cross-cutting: it links into all 10 grass-type
// guides so a reader landing here can find the task-specific guidance for
// their species. The grass-type guides reciprocally link back via the
// PillarCrossLinks component.

import { amazonUrl } from '@/lib/affiliate'

import type { AuthorSlug } from "@/lib/types/author"
import type { ProductRecommendation } from "@/data/guide-product-recommendations"

export interface CareTaskPillarSection {
  id: string
  title: string
  /** HTML content. Keep paragraphs short; use h3 for subheadings. */
  content: string
  /** Optional inline ASIN-tagged product picks rendered below this section's
   * content via `GuideProductCard`. Source ASINs via `npm run paapi:lookup`. */
  productPicks?: ProductRecommendation[]
}

export interface CareTaskPillarFaq {
  question: string
  answer: string
}

export interface CareTaskPillarSource {
  label: string
  url: string
  publisher?: string
}

export interface CareTaskHowToStep {
  name: string
  text: string
  url?: string
}

export interface CareTaskHowToBlock {
  name: string
  description?: string
  totalTime?: string
  supplies?: string[]
  tools?: string[]
  steps: CareTaskHowToStep[]
}

export interface CareTaskPillar {
  slug: string
  title: string
  metaTitle: string
  metaDescription: string
  heroSubtitle: string
  authorSlug: AuthorSlug
  /** Calculator tool slug to feature inline (e.g. "fertilizer-calculator"). */
  toolSlug: string
  toolLabel: string
  toolDescription: string
  // Immutable original publish date. Used for Article.datePublished in JSON-LD.
  // Set once, never bump on edits; bump `lastUpdated` instead.
  firstPublished: string
  // Most recent meaningful edit. Used for Article.dateModified and the
  // sitemap lastmod. Update whenever content changes.
  lastUpdated: string
  sections: CareTaskPillarSection[]
  /** Optional FAQ block, surfaced as FAQPage JSON-LD for AI/LLM citation. */
  faqs?: CareTaskPillarFaq[]
  keyTakeaways?: string[]
  sources?: CareTaskPillarSource[]
  howTos?: CareTaskHowToBlock[]
  keywords?: string[]
  /** Copy for the dark closing CTA card (funnels to the pillar's calculator).
   *  Falls back to a generic prompt when omitted. The hero image itself is
   *  admin-managed via the Directus `guide_images` collection. */
  endCta?: { title: string; description: string }
}

const fertilizing: CareTaskPillar = {
  slug: "fertilizing",
  title: "Lawn Fertilizing: A Complete Guide",
  metaTitle: "Lawn Fertilizing Guide | When, How Much, Which NPK",
  metaDescription:
    "How to fertilize a lawn the right way. Timing, NPK rates, application methods, and a calculator that does the math for your grass type and lawn size.",
  heroSubtitle:
    "When to fertilize, how much to apply, and which NPK ratio your grass actually needs. The basics, the math, and where every grass type fits.",
  authorSlug: "james-thornton",
  toolSlug: "fertilizer-calculator",
  toolLabel: "Lawn Fertilizer Calculator",
  toolDescription:
    "Plug in your lawn size and grass type to get exact NPK rates and pounds-per-1,000-sqft.",
  firstPublished: "2026-05-02",
  lastUpdated: "2026-05-06",
  endCta: {
    title: "Get the exact rate for your lawn",
    description:
      "Stop guessing pounds per bag. Enter your lawn size and grass type and the calculator does the back-of-bag math for you.",
  },
  keyTakeaways: [
    "Pounds of nitrogen per 1,000 square feet is the unit that matters.",
    "Cool-season grass wants 2 to 4 pounds of nitrogen per 1,000 sqft per year; warm-season 1 to 4.",
    "Skip phosphorus on established lawns unless a soil test calls for it.",
    "Time applications to your grass type: cool-season feeds in fall, warm-season in summer.",
    "Slow-release sources resist burn; high-analysis bags cover more square feet per pound.",
  ],
  howTos: [
    {
      name: "How to apply granular fertilizer correctly",
      description: "The repeatable method for spreading granular lawn fertilizer at the right rate without striping or burning.",
      totalTime: "PT30M",
      tools: ["Broadcast or drop spreader", "Calibrated rate chart"],
      supplies: ["Granular fertilizer", "Hose or sprinkler"],
      steps: [
        {
          name: "Calculate the rate",
          text: "Divide 1 by the nitrogen percentage on the bag to get pounds of product per 1,000 sqft for a 1 lb N rate. A 16-4-8 bag needs 6.25 lbs per 1,000 sqft.",
        },
        {
          name: "Set the spreader",
          text: "Look up your spreader's setting for your product on the rate chart. When in doubt, set low and make two passes at half rate.",
        },
        {
          name: "Apply on dry grass",
          text: "Spread fertilizer when the grass blades are dry so granules fall through to the soil instead of sticking to the leaves.",
        },
        {
          name: "Use perpendicular passes",
          text: "Walk the perimeter once, then fill in the interior in stripes. Make the second pass perpendicular to the first for even coverage.",
        },
        {
          name: "Water it in",
          text: "Apply a quarter to a half inch of water within 24 hours to move the fertilizer into the root zone and reduce burn risk.",
        },
      ],
    },
  ],
  sources: [
    {
      publisher: "UGA Extension",
      label: "Turfgrass Fertility: Understanding Fertilizer Labels, Macronutrients, and Micronutrients",
      url: "https://fieldreport.caes.uga.edu/publications/C1058-2/turfgrass-fertility-understanding-fertilizer-labels-macronutrients-and-micronutrients/",
    },
    {
      publisher: "NC State Extension",
      label: "Carolina Lawns: A Guide to Maintaining Quality Turf",
      url: "https://content.ces.ncsu.edu/catalog/series/227/",
    },
    {
      publisher: "Penn State Extension",
      label: "Turfgrass Fertilization: A Basic Guide for Professional Turfgrass Managers",
      url: "https://extension.psu.edu/turfgrass-fertilization-a-basic-guide-for-professional-turfgrass-managers",
    },
    {
      publisher: "University of Florida IFAS",
      label: "Florida Lawn Fertilizer Regulations",
      url: "https://edis.ifas.ufl.edu/publication/EP236",
    },
  ],
  sections: [
    {
      id: "fundamentals",
      title: "Fertilizer fundamentals",
      productPicks: [
        {
          name: "The Andersons PGF Complete 16-4-8",
          description:
            "Balanced slow-release granular fertilizer that demonstrates the textbook 16-4-8 ratio described in this section.",
          category: "Fertilizer",
          affiliateUrl:
            amazonUrl('B07SYLCKRH'),
          price: "$60",
          productsPageFilter: "Fertilizers & Soil Health",
          imageUrl: "https://m.media-amazon.com/images/I/41xelE+7eJL._AC_SL200_.jpg",
        },
        {
          name: "Milorganite 6-4-0",
          description:
            "Lower-nitrogen slow-release option for lawns that burn easily, useful when the math says less N, not more.",
          category: "Fertilizer",
          // 2026-07-11: swapped from OOS B0DDHTJKCY to the in-stock 32 lb listing
          // (B0FGDNF55M, $34.84). Same product/size; consolidates Milorganite.
          affiliateUrl:
            amazonUrl('B0FGDNF55M'),
          price: "$35",
          productsPageFilter: "Fertilizers & Soil Health",
          imageUrl: "https://m.media-amazon.com/images/I/71nBKo4i15L._AC_SL200_.jpg",
        },
        {
          name: "Scotts Turf Builder Starter Food for New Grass",
          description:
            "Starter lawn food with phosphorus for seeding or new sod, the one situation where higher P actually matters.",
          category: "Fertilizer",
          affiliateUrl:
            amazonUrl('B00DK49DVA'),
          price: "$30",
          productsPageFilter: "Fertilizers & Soil Health",
          imageUrl: "https://m.media-amazon.com/images/I/81oY9zpw+qL._AC_SL200_.jpg",
        },
      ],
      content: `
        <p>Three numbers run lawn fertilizing. The N, P, and K on every bag (nitrogen, phosphorus, potassium) tell you what the grass actually gets. Nitrogen drives leaf growth and color. Phosphorus matters mostly at seeding time and during root establishment. Potassium does the unglamorous work of stress tolerance, disease resistance, and winter hardiness. A bag labeled 24-0-6 is 24% nitrogen, 0% phosphorus, 6% potassium by weight, and the remaining mass is filler and secondary nutrients (see <a href="https://fieldreport.caes.uga.edu/publications/C1058-2/turfgrass-fertility-understanding-fertilizer-labels-macronutrients-and-micronutrients/" target="_blank" rel="noopener noreferrer">UGA Extension's fertilizer label breakdown</a> for the full anatomy of a bag).</p>
        <p>The cardinal rule is: pounds of nitrogen per 1,000 square feet, applied at the right time of year for your grass. Too little and the lawn thins. Too much and you get surge growth, disease pressure, and burn. Most healthy lawns need about 2 to 4 pounds of total nitrogen per 1,000 sqft per year for cool-season grass, and 1 to 4 pounds for warm-season, split across several applications. Those ranges come straight from <a href="https://content.ces.ncsu.edu/catalog/series/227/" target="_blank" rel="noopener noreferrer">NC State's Carolina Lawns guide</a> and align with what <a href="https://extension.psu.edu/turfgrass-fertilization-a-basic-guide-for-professional-turfgrass-managers" target="_blank" rel="noopener noreferrer">Penn State Extension's turf fertilization guide</a> recommends for the northeast.</p>
        <p>Not every lawn needs phosphorus. If your soil test shows adequate P, skip it. Many states (Florida is the strictest example) restrict or ban P on established turf except where a soil test proves a deficiency or you are establishing new grass.</p>
      `,
    },
    {
      id: "reading-the-bag",
      title: "Reading a fertilizer bag (and the math behind it)",
      content: `
        <p>The most useful skill in lawn care is the back-of-bag math. Take 16-4-8: that's 16% nitrogen by weight. A 50-pound bag of 16-4-8 contains 8 pounds of actual nitrogen (50 x 0.16). To deliver the standard 1 pound of N per 1,000 sqft, you need 6.25 pounds of product per 1,000 sqft (1 / 0.16). UGA puts the same example in their <a href="https://fieldreport.caes.uga.edu/publications/C1058-2/turfgrass-fertility-understanding-fertilizer-labels-macronutrients-and-micronutrients/" target="_blank" rel="noopener noreferrer">turfgrass fertility publication</a>, and it's the calculation our <a href="/tools/fertilizer-calculator">fertilizer calculator</a> runs for you automatically.</p>
        <p>Common ratios you'll see and what they're for:</p>
        <ul>
          <li><strong>16-4-8 or 12-4-8</strong>: the workhorse 4-1-2 ratio for routine feedings on most lawns.</li>
          <li><strong>24-0-6, 28-0-3, 32-0-4</strong>: high-nitrogen maintenance blends, often slow-release, no phosphorus (legal in P-restricted states).</li>
          <li><strong>10-10-10 or 13-13-13</strong>: balanced ag-store blends. Fine for gardens, usually wrong for established lawns because the P is wasted and the N percentage is low (you have to spread a lot of product to hit your N rate).</li>
          <li><strong>15-0-15 or 0-0-7</strong>: high-potassium blends used in fall for winter hardiness or on coastal lawns dealing with salt stress. Common pick for centipede in the southeast.</li>
          <li><strong>18-24-12 starter</strong>: phosphorus-heavy starter fertilizer for new seed or sod where roots haven't established yet.</li>
          <li><strong>20-0-10 or similar</strong>: mid-N maintenance, P-free, K for stress tolerance. Popular on Bermuda.</li>
        </ul>
        <p>If two products both deliver 1 lb N / 1,000 sqft, the higher-analysis bag (say 32-0-4) covers more square feet per pound and usually costs less per pound of N applied, but it's also easier to burn with. The lower-analysis bag (10-10-10) is more forgiving but wastes phosphorus and potassium most established lawns don't need.</p>
      `,
    },
    {
      id: "soil-test",
      title: "Soil testing: the step almost everyone skips",
      content: `
        <p>A $12 to $25 soil test from your state extension lab is the highest-leverage thing you can do for a lawn. It tells you actual pH (most fertilizer becomes unavailable below 5.5 or above 7.5), phosphorus and potassium levels, and a custom fertilizer recommendation for the grass you grow. <a href="https://soiltesting.tamu.edu/" target="_blank" rel="noopener noreferrer">Texas A&M's Soil, Water and Forage Testing Lab</a>, <a href="https://agrilifeextension.tamu.edu/assets/environment-natural-resources/soil/soil-testing/" target="_blank" rel="noopener noreferrer">AgriLife Extension's how-to</a>, and most other state extension labs offer the same service for around the same price. <a href="https://www.epa.gov/nutrientpollution/what-you-can-do-your-yard" target="_blank" rel="noopener noreferrer">EPA's nutrient pollution guidance</a> also recommends testing before fertilizing as a runoff-reduction step.</p>
        <p>Pull a composite sample: 6 to 10 plugs about 4 inches deep from across the lawn, mixed in a clean bucket, then about a cup sent in. Test every 2 to 3 years (every year if you're correcting pH). The results dictate whether you actually need P and K, how much lime or sulfur to apply, and whether your "the lawn looks bad" problem is a fertility problem at all (often it's compaction or thatch instead).</p>
      `,
    },
    {
      id: "timing-by-season",
      title: "Timing by grass type",
      content: `
        <p>Cool-season grasses (Kentucky Bluegrass, Tall Fescue, Fine Fescue, Perennial Ryegrass) take their heaviest feedings in fall, when roots are growing aggressively and the plant is storing energy for winter. A typical schedule: light feeding in early spring (April), optional light summer maintenance, then the two big feedings around Labor Day and again in late October ahead of dormancy. <a href="https://extension.psu.edu/lawn-management-through-the-seasons" target="_blank" rel="noopener noreferrer">Penn State's seasonal lawn management calendar</a> walks through the month-by-month timing for the northeast.</p>
        <p>Warm-season grasses (Bermuda, Zoysia, St. Augustine, Centipede, Bahia, Buffalograss) feed during their active summer growth window. Don't fertilize warm-season grass before it's fully greened up in spring; you'll wake the wrong things first (weeds and roots before shoots). UGA's rule of thumb: hold the first nitrogen application until <a href="https://fieldreport.caes.uga.edu/publications/C1009/ten-steps-to-a-healthier-home-lawn/" target="_blank" rel="noopener noreferrer">soil temperature at 4 inches is consistently 65°F</a>. For most of the south, that's late April or early May. <a href="https://agrilifeextension.tamu.edu/library/landscaping/bermudagrass-home-lawn-management-calendar/" target="_blank" rel="noopener noreferrer">Texas A&M's Bermudagrass Home Lawn Calendar</a> spells out the warm-season cadence for the gulf states.</p>
        <p>Centipede is the exception that proves the rule: it wants very little nitrogen (1 to 2 pounds per 1,000 sqft per year, total) and gets sick from over-fertilization. A 15-0-15 in early summer is often all it needs. Centipede decline is almost always a too-much-nitrogen story.</p>
      `,
    },
    {
      id: "nitrogen-sources",
      title: "Nitrogen sources compared",
      content: `
        <p>The N on a bag isn't all the same. The form of nitrogen affects how fast it greens, how long it lasts, how easily it burns, and how much leaches into groundwater. <a href="https://turf.purdue.edu/fertilizer-recommendations/" target="_blank" rel="noopener noreferrer">Purdue Turfgrass Science</a> recommends mixing fast and slow sources, weighted slow-release in spring and summer, fast-release in fall.</p>
        <ul>
          <li><strong>Urea (46-0-0)</strong>: cheapest N per pound, water-soluble, greens up in days. Volatilizes (gases off) within hours if not watered in. The default in commercial blends.</li>
          <li><strong>Ammonium sulfate (21-0-0)</strong>: fast, acidifies soil (good for high-pH lawns), high burn risk. Common on southern lawns where alkaline soil is the bigger problem.</li>
          <li><strong>Sulfur-coated urea (SCU) and polymer-coated urea (PCU)</strong>: slow-release, feed for 6 to 12 weeks, low burn risk. The backbone of most premium homeowner blends.</li>
          <li><strong>Methylene urea / urea formaldehyde</strong>: long-lasting slow-release, breaks down via soil microbes. Temperature-dependent, so it slows in cool weather.</li>
          <li><strong>Milorganite and feather meal</strong>: organic slow-release. Lower N (5 to 13%), low burn risk, also adds iron and trace nutrients. Good for centipede, St. Augustine, and lawns near water bodies.</li>
        </ul>
        <p>Practical rule: in spring and summer, use products with at least 30 to 50% slow-release N (it'll say on the bag, often as "WIN" or "water-insoluble nitrogen"). In late fall, a faster source greens the lawn before dormancy without driving excess shoot growth.</p>
      `,
    },
    {
      id: "application-methods",
      title: "Granular vs liquid, slow vs fast",
      content: `
        <p>Granular fertilizer is what most homeowners reach for: spread it with a broadcast spreader, water it in, walk away. Slow-release granules feed the lawn for 6 to 8 weeks. Quick-release is cheaper and acts in days but burns more easily and leaches faster.</p>
        <p>Liquid fertilizer (or fertigation through a sprinkler) is faster but harder to dose evenly. Most professional crews use granular slow-release as the backbone and only liquid-spike when they want a quick green-up before an event.</p>
        <p>Whatever the form, water in lightly (about a quarter inch) within 24 hours unless the bag says otherwise. Dry granules sitting on hot turf are how you get fertilizer burn streaks. Don't apply if heavy rain is in the forecast inside that 24-hour window either; runoff is the worst-case outcome and the reason behind <a href="https://edis.ifas.ufl.edu/publication/EP353" target="_blank" rel="noopener noreferrer">Florida's Urban Turf Fertilizer Rule</a> and similar regulations elsewhere.</p>
      `,
    },
    {
      id: "liquid-feeding",
      title: "Going all-liquid: when it makes sense",
      content: `
        <p>If you would rather spray than spread, a liquid-only program is very doable, and it shines in summer when a heavy granular nitrogen load risks burning the lawn. Liquids green up in days, apply evenly through a hose-end sprayer, and let you feed little and often instead of in big hits.</p>
        <p>The tradeoff is longevity: a liquid feed runs about 2 to 4 weeks against 6 to 8 for slow-release granular, so you spray more often. The trick is matching the formula to the job: a balanced 16-4-8 to maintain, a high-nitrogen 28-0-0 to push color, a phosphorus-free 15-0-15 for summer hardiness, iron for color without growth, and a 3-18-18 starter for new seed.</p>
        <p>We ranked the leading options by need in the <a href="/guides/best-liquid-lawn-fertilizer">best liquid lawn fertilizer guide</a>, with a side-by-side comparison and how to apply each one.</p>
      `,
    },
    {
      id: "common-mistakes",
      title: "Common fertilizing mistakes",
      content: `
        <p>The patterns we see over and over:</p>
        <ul>
          <li><strong>Fertilizing dormant grass.</strong> Warm-season lawns getting a March feeding before greenup just feed the winter weeds. Cool-season lawns getting heavy summer N during heat dormancy invite disease.</li>
          <li><strong>Skipping the soil test.</strong> Adding P or K the lawn doesn't need wastes money and pollutes runoff. Add only what the lab says is missing.</li>
          <li><strong>Spreader streaks.</strong> Dialed wrong, drop spreaders show every pass as a green stripe within a week. Calibrate against the bag's settings chart and apply half the rate in two perpendicular passes for evenness.</li>
          <li><strong>Watering in too much or too little.</strong> A quarter inch is the target. Heavy watering after granular leaches nitrogen past the roots; no watering leaves urea to volatilize.</li>
          <li><strong>Weed-and-feed timing.</strong> The fertilizer side wants warm, actively growing turf; the herbicide side wants the right weed stage. They rarely line up perfectly. Spot-spraying weeds and fertilizing separately almost always beats a combo bag.</li>
          <li><strong>Fall fertilizer on warm-season grass.</strong> Late N on Bermuda or Zoysia delays dormancy, weakens cold tolerance, and invites spring dead spot. Stop feeding warm-season grass at least 6 weeks before your average first frost.</li>
        </ul>
        <p>The <a href="https://www.usga.org/course-care/turfgrass-and-environmental-research/research-updates/seven-things-you-can-do-to-reduce-nutrient-loss.html" target="_blank" rel="noopener noreferrer">USGA Green Section</a> has a useful nutrient-loss checklist that overlaps heavily with this list, written for golf courses but applicable to home lawns.</p>
      `,
    },
  ],
  faqs: [
    {
      question: "How much nitrogen does a lawn need per year?",
      answer:
        "Most healthy lawns need 2 to 4 pounds of total nitrogen per 1,000 square feet per year, split across several applications. Centipede is the exception and wants far less.",
    },
    {
      question: "When should I fertilize cool-season grass?",
      answer:
        "Cool-season grasses (Kentucky Bluegrass, Tall Fescue, Fine Fescue, Perennial Ryegrass) take their heaviest feedings in fall. A typical schedule is a light feeding in early spring, optional summer maintenance, and the two largest feedings in September and late October.",
    },
    {
      question: "When should I fertilize warm-season grass?",
      answer:
        "Warm-season grasses (Bermuda, Zoysia, St. Augustine, Centipede, Bahia, Buffalograss) feed during their active summer growth window. Wait until the lawn has fully greened up in spring before the first application.",
    },
    {
      question: "What does the NPK number on a fertilizer bag mean?",
      answer:
        "NPK is the percentage by weight of nitrogen, phosphorus, and potassium in the bag. A 24-0-6 bag is 24% nitrogen, 0% phosphorus, 6% potassium. Nitrogen drives leaf growth, phosphorus matters at seeding, and potassium supports stress tolerance.",
    },
    {
      question: "Is granular or liquid fertilizer better for lawns?",
      answer:
        "Granular slow-release fertilizer is the residential workhorse: easy to spread, feeds for 6 to 8 weeks, low burn risk. Liquid is faster but harder to dose evenly. Most professional crews use granular as the backbone and only liquid-spike before an event.",
    },
    {
      question: "Is 10-10-10 fertilizer good for lawns?",
      answer:
        "It works but it's rarely the best choice for established turf. The phosphorus is wasted on most lawns (and illegal to apply on established turf in some states), and the 10% nitrogen means you have to spread a lot of product to hit a 1 lb N / 1,000 sqft rate. A 16-4-8, 20-0-10, or 24-0-6 is more efficient. Save 10-10-10 for vegetable gardens or new seeding.",
    },
    {
      question: "What does 16-4-8 fertilizer do for Bermuda grass?",
      answer:
        "16-4-8 delivers a balanced 4-1-2 ratio that matches what most extension services recommend for warm-season turf. A 50-pound bag of 16-4-8 contains 8 pounds of nitrogen; spreading 6.25 pounds per 1,000 sqft delivers the standard 1 lb of N. Bermuda typically wants 4 to 5 of those applications across the active growing season.",
    },
    {
      question: "How do I calculate how much nitrogen is in my fertilizer?",
      answer:
        "Multiply the bag weight by the first number on the label divided by 100. A 50-pound bag of 24-0-6 contains 50 x 0.24 = 12 pounds of nitrogen. To apply 1 lb of N per 1,000 sqft from a 24-0-6 bag, you spread 4.17 pounds of product per 1,000 sqft (1 / 0.24). The fertilizer calculator on this site does the math automatically.",
    },
    {
      question: "Should I get a soil test before fertilizing?",
      answer:
        "Yes, especially before adding phosphorus or potassium. A $12 to $25 soil test from your state extension lab tells you actual pH, P, and K levels and gives a custom fertilizer recommendation. Test every 2 to 3 years, or annually if you're correcting pH. Many fertility problems people try to fix with more nitrogen are actually pH or compaction issues.",
    },
    {
      question: "Is organic fertilizer better than synthetic for lawns?",
      answer:
        "Both work; the choice depends on goals. Organic sources (Milorganite, feather meal, composted manures) release slowly, are nearly impossible to burn with, and improve soil microbiology over time, but cost more per pound of nitrogen. Synthetic blends are cheaper, faster-acting, and easier to dose precisely. A common middle path is organic in spring and summer, synthetic for the late-fall feeding.",
    },
  ],
}

const weedControl: CareTaskPillar = {
  slug: "weed-control",
  title: "Lawn Weed Control: A Complete Guide",
  metaTitle: "Lawn Weed Control Guide | Pre-Emergent, Post-Emergent, Timing",
  metaDescription:
    "How to control lawn weeds the right way. Pre-emergent timing by region, post-emergent product selection, and tools that calculate your application window.",
  heroSubtitle:
    "Pre-emergent before weeds germinate, post-emergent for what got through, and a thick lawn doing the heavy lifting in between.",
  authorSlug: "sarah-mitchell",
  toolSlug: "herbicide-timing",
  toolLabel: "Herbicide Timing Calculator",
  toolDescription:
    "Enter your ZIP and we tell you exactly when your pre-emergent or post-emergent window opens.",
  firstPublished: "2026-05-02",
  lastUpdated: "2026-05-06",
  endCta: {
    title: "Time your weed control right",
    description:
      "Pre-emergent and post-emergent both live or die by timing. Enter your ZIP and grass type and get the window that actually works for your weeds.",
  },
  keyTakeaways: [
    "Pre-emergent prevents seeds from germinating. Post-emergent kills what grew anyway.",
    "Time pre-emergent to soil temperature, not the calendar. 50 to 55 degree soil triggers crabgrass.",
    "Identify the weed before you spray. Wrong herbicide on the wrong weed wastes money and time.",
    "A thick, properly mowed lawn is the most effective long-term weed control.",
    "Spot-treat instead of blanket-spraying when you can.",
  ],
  howTos: [
    {
      name: "How to apply pre-emergent herbicide",
      description: "The repeatable method for timing and applying pre-emergent so it prevents annual weeds before they germinate.",
      totalTime: "PT45M",
      tools: ["Broadcast or drop spreader (granular)", "Sprayer (liquid)"],
      supplies: ["Pre-emergent herbicide", "Hose for watering in"],
      steps: [
        {
          name: "Track soil temperature",
          text: "Watch local soil temperatures at a 2-inch depth. Apply once the rolling 5-day average hits 50 to 55 degrees Fahrenheit, before crabgrass germinates.",
        },
        {
          name: "Calculate the rate",
          text: "Multiply your lawn square footage by the label's per-1,000-sqft rate. Most pre-emergents use 1 to 2.5 pounds of product per 1,000 sqft.",
        },
        {
          name: "Apply uniformly",
          text: "Spread with a broadcast spreader in two perpendicular passes at half-rate each. Skipped strips become weed strips.",
        },
        {
          name: "Water it in",
          text: "Most granular pre-emergents need a quarter to a half inch of water within 7 days to activate the chemical barrier.",
        },
        {
          name: "Plan the second application",
          text: "A single application protects 8 to 12 weeks. Re-apply mid-summer with a split-application program if you have heavy weed pressure or sandy soils.",
        },
      ],
    },
  ],
  sources: [
    {
      publisher: "NC State Extension",
      label: "Pre-Emergence Herbicides for Crabgrass Control",
      url: "https://content.ces.ncsu.edu/catalog/series/227/",
    },
    {
      publisher: "Purdue Turf Science",
      label: "Preventing Crabgrass",
      url: "https://extension.purdue.edu/news/county/whitley/2022/03/preventing-crabgrass.html",
    },
    {
      publisher: "Penn State Extension",
      label: "Weed Identification and Lawn Weed Control Calendar",
      url: "https://extension.psu.edu/trees-lawns-and-landscaping/turfgrass-and-lawn-care/weed-management",
    },
  ],
  sections: [
    {
      id: "diagnosis-first",
      title: "Diagnose before you spray",
      content: `
        <p>Most failed weed control comes from spraying the wrong product at the wrong time. Pre-emergents stop seeds from germinating. They do nothing to weeds that already have leaves above the soil. Post-emergents kill growing weeds. They do nothing to ungerminated seeds. If you spray a pre-emergent on a yard full of crabgrass, you've just bought a 90-day delay before you can re-seed and watched the existing crabgrass laugh at you.</p>
        <p>So step one is always: identify what's there. Annual grassy weeds (crabgrass, Poa annua, goosegrass, foxtail) need a pre-emergent strategy. Broadleaf perennials (dandelion, clover, plantain, ground ivy, violet) need a post-emergent and a thicker lawn next year. Sedges (yellow and purple nutsedge) need their own product class entirely (halosulfuron or sulfentrazone), since standard broadleaf sprays do almost nothing to them.</p>
        <p>If you can't ID the weed by sight, the <a href="https://extension.psu.edu/weed-management-in-turf" target="_blank" rel="noopener noreferrer">Penn State Extension weed management library</a> has photo keys for the most common turf weeds in the U.S.</p>
      `,
    },
    {
      id: "common-weeds",
      title: "Common lawn weeds: a quick ID guide",
      content: `
        <h3>Annual grassy weeds</h3>
        <p><a href="/weeds/crabgrass">Crabgrass</a> is the headline annual: light green, low-growing, spreads in star-shaped tufts that radiate from a center crown by midsummer. <a href="/weeds/goosegrass">Goosegrass</a> looks similar but the center crown is bright white and flatter. Both die at first frost but drop seed first, which is why pre-emergent timing matters every year. <a href="/weeds/annual-bluegrass-poa-annua">Annual bluegrass (Poa annua)</a> flips the calendar: it germinates in fall, looks like a finer, lighter version of Kentucky bluegrass through winter, and seeds out in spring before dying in early summer heat. <a href="/weeds/foxtail-setaria">Foxtail</a> rounds out the group, standing more upright than crabgrass with a fuzzy bottlebrush seed head by late summer.</p>
        <h3>Broadleaf perennials</h3>
        <p><a href="/weeds/dandelions">Dandelion</a>, <a href="/weeds/white-clover">white clover</a>, <a href="/weeds/broadleaf-plantain">broadleaf plantain</a>, <a href="/weeds/creeping-charlie-ground-ivy">ground ivy (creeping charlie)</a>, and <a href="/weeds/wild-violet">wild violet</a> are the five that show up on most homeowner lists. Dandelion and plantain are easy kills with a 3-way (2,4-D + dicamba + MCPP). Ground ivy and violet are stubborn and usually need triclopyr or a fall application of a 4-way to actually finish the job. Cool-season winter annuals like <a href="/weeds/henbit">henbit</a> and <a href="/weeds/chickweed-common">common chickweed</a>, plus the mat-forming summer annual <a href="/weeds/spotted-spurge">spotted spurge</a>, follow the same broadleaf playbook.</p>
        <h3>Sedges</h3>
        <p><a href="/weeds/nutsedge-yellow">Yellow nutsedge</a> stands out because it grows about 50% faster than the surrounding lawn. A few days after mowing, you'll see lighter, glossy spikes sticking up an inch above the canopy. Roll a stem between your fingers: sedges have triangular stems, grasses are round or flat. Sedges thrive in soggy spots, so a drainage fix often does as much as the herbicide.</p>
      `,
    },
    {
      id: "pre-emergent-timing",
      title: "Pre-emergent timing by soil temperature",
      productPicks: [
        {
          name: "The Andersons Barricade Granular Pre-Emergent",
          description:
            "Granular prodiamine to lay down before soil holds at the 55°F crabgrass trigger described above; water in within 2 to 3 days to set the barrier.",
          category: "Pre-emergent",
          affiliateUrl:
            amazonUrl('B07NZ2BMMK'),
          price: "$47",
          productsPageFilter: "Weed Control",
          imageUrl: "https://m.media-amazon.com/images/I/41goftL2ddL._AC_SL200_.jpg",
        },
      ],
      content: `
        <p>Crabgrass germinates when soil temperature at the upper 1 to 2 inches holds at 55°F for several consecutive days. <a href="https://www.canr.msu.edu/news/timing_crabgrass_pre_emergence_applications" target="_blank" rel="noopener noreferrer">Michigan State University Extension</a> recommends applying when soil temps consistently reach 50 to 55°F at the 0 to 2 inch depth, so the chemical barrier is in place before the first germination flush. <a href="https://extension.umd.edu/resource/crabgrass" target="_blank" rel="noopener noreferrer">University of Maryland Extension</a> uses the same 53 to 55°F threshold for 5 consecutive days as the trigger.</p>
        <p>That's why pre-emergents go down in early spring before the first cut, not by calendar. <a href="https://extension.psu.edu/weed-management-in-turf" target="_blank" rel="noopener noreferrer">Penn State Extension</a> publishes regional windows: southeastern PA runs March 15 to April 15, central PA April 1 to May 1, and northern or high-altitude areas April 20 to May 10. In the deep south the same logic puts the window in late February. ZIP-code-driven timing matters more than rule-of-thumb dates, and our <a href="/tools/herbicide-timing">herbicide timing calculator</a> uses your ZIP plus current soil temps to set the window.</p>
        <p>Forsythia bloom is the old folk indicator and it's still useful as a "start watching soil temps" reminder, but it's not reliable on its own. Forsythia responds to air temperature and day length while crabgrass responds to soil temperature, so a warm February air mass can pop the blooms while soils stay in the mid-40s.</p>
        <p>For Poa annua, the window flips: apply in late summer or early fall, before fall germination begins (target soil temps falling through 70°F at the 2-inch depth, usually mid-August through September depending on latitude). Two spring crabgrass applications won't touch Poa, since it has already germinated and put down roots months earlier.</p>
        <p>Most pre-emergents need 0.5 inch of irrigation or rainfall within 2 to 3 days of application to activate the soil barrier. Without that water-in step, the active ingredient sits on the canopy and breaks down in UV. A second, lower-rate application 6 to 8 weeks later extends the barrier through summer for late-germinating species like goosegrass.</p>
      `,
    },
    {
      id: "post-emergent-strategy",
      title: "Post-emergent strategy",
      productPicks: [
        {
          name: "Ortho Weed B Gon Plus Crabgrass Control (Ready-to-Spray)",
          description:
            "Hose-end quinclorac formula for spot-treating crabgrass and broadleaf weeds without mixing a separate sprayer, matching the spot-treat approach here.",
          category: "Post-emergent",
          affiliateUrl:
            amazonUrl('B005LD2GNK'),
          price: "$25",
          productsPageFilter: "Weed Control",
          imageUrl: "https://m.media-amazon.com/images/I/41B5D141ytL._AC_SL200_.jpg",
        },
        {
          name: "Syngenta Tenacity Turf Herbicide",
          description:
            "Mesotrione concentrate for the tough and newly-seeded-lawn cases where standard 3-way broadleaf killers can't be used; a little goes a long way in a pump sprayer.",
          category: "Post-emergent",
          affiliateUrl:
            amazonUrl('B005DUTNF0'),
          price: "$61",
          productsPageFilter: "Weed Control",
          imageUrl: "https://m.media-amazon.com/images/I/41tEE7n3sRL._AC_SL200_.jpg",
        },
      ],
      content: `
        <p>Spot-treat where you can. Blanket spraying a whole lawn for three dandelions wastes product and stresses the turf. A pump sprayer with a 3-way (2,4-D + dicamba + MCPP or MCPA) handles 90% of homeowner broadleaf problems at a fraction of the cost of a hose-end blanket application.</p>
        <p>Spray when daytime highs are 60 to 85°F, weeds are actively growing, and there's no rain in the forecast for at least 24 hours. Hot summer applications above 90°F burn the lawn alongside the weeds and volatilize 2,4-D and dicamba into vapor that can drift onto neighbors' tomatoes and ornamentals. Fall applications (late September through early November in most of the country) are the single most effective broadleaf treatment of the year, because perennial weeds are pulling carbohydrates down into roots and the herbicide goes with them.</p>
        <p>Wait 2 to 3 days after mowing before you spray (you need leaf area to absorb the herbicide), and wait another 2 to 3 days after spraying before mowing again (so the systemic herbicide can translocate). Don't water for 24 hours after a foliar broadleaf application unless the label says otherwise.</p>
        <p>Selective broadleaf herbicides leave most lawn grasses alone but kill many broadleaf weeds. Read the label for grass safety: St. Augustine and Centipede are sensitive to several common active ingredients (high rates of 2,4-D burn St. Augustine; dicamba can damage Centipede). For warm-season lawns, atrazine and metsulfuron are common label-safe alternatives, with their own restrictions.</p>
      `,
    },
    {
      id: "modes-of-action",
      title: "Herbicide modes of action, in plain English",
      content: `
        <p>Every herbicide label includes a WSSA group number (or HRAC letter). The number tells you how the chemical kills the plant, which matters for two reasons: rotating modes of action prevents resistance, and you can swap brand names if a product is out of stock as long as the group number matches. The full <a href="https://wssa.net/wp-content/uploads/WSSA-Mechanism-of-Action.pdf" target="_blank" rel="noopener noreferrer">WSSA herbicide mode-of-action chart</a> is the canonical reference.</p>
        <p>The handful that matter for home lawns:</p>
        <ul>
          <li><strong>Group 3 (mitosis inhibitors):</strong> prodiamine (Barricade), pendimethalin (Pendulum), benefin. Pre-emergents that stop root cell division in germinating seeds. Long residual, 3 to 5 months.</li>
          <li><strong>Group 4 (synthetic auxins):</strong> 2,4-D, dicamba, MCPP, MCPA, triclopyr. The classic broadleaf killers. Cause uncontrolled growth that the plant can't sustain. Kills most broadleaf weeds, safe on most grasses at label rate.</li>
          <li><strong>Group 5 (PSII inhibitors):</strong> atrazine, simazine. Pre and post-emergent for warm-season lawns; not safe on cool-season turf.</li>
          <li><strong>Group 9 (EPSP synthase inhibitor):</strong> glyphosate (Roundup). Non-selective; kills everything green. Use only for spot kills, edging, or full renovations.</li>
          <li><strong>Group 14 (PPO inhibitors):</strong> sulfentrazone (Dismiss), carfentrazone. Fast-acting contact herbicides for sedges and tough broadleaves.</li>
          <li><strong>Group 27 (HPPD inhibitors):</strong> mesotrione (Tenacity). Bleaches new weed growth white. Safe at seeding, which is rare among post-emergents.</li>
        </ul>
        <p>If a 3-way isn't finishing off ground ivy after two applications, swap to a different group (Group 4 triclopyr or Group 27 mesotrione) instead of just spraying more of the same.</p>
      `,
    },
    {
      id: "selective-vs-nonselective",
      title: "Selective vs non-selective, spot vs broadcast",
      content: `
        <p>Selective herbicides kill some plants and leave others. Most lawn herbicides are selective for broadleaf weeds in grass turf. Non-selective herbicides (glyphosate is the main one) kill anything green, so they belong in driveway cracks, fence lines, and full lawn renovations, not on a healthy lawn.</p>
        <p>Spot-spraying with a pump sprayer treats individual weeds. It uses 90% less product, costs less per season, and avoids the off-target stress of blanket coverage. Broadcast spraying (hose-end or spreader-applied granules) covers the entire lawn at a uniform rate. It makes sense when more than about 30% of the lawn is weeds, since spot-spraying that many targets gets impractical and you'll miss seedlings.</p>
        <p>For a typical 5,000 sq ft suburban lawn with scattered dandelions and clover, a 1-gallon pump sprayer of mixed 3-way handles a full pass in under 30 minutes. Reserve granular weed-and-feed or hose-end blanket products for heavily infested or newly bought lawns where the cost-benefit flips.</p>
      `,
    },
    {
      id: "weed-and-feed",
      title: "Weed-and-feed: when it actually makes sense",
      content: `
        <p>Combination weed-and-feed products solve a marketing problem (one trip to the store, one trip across the lawn) and create an agronomic one. The fertilizer in the bag wants to go down when grass is actively growing and pulling nitrogen, which is mid-spring through early fall in cool-season regions. The herbicide wants to go down when weeds are emerged or about to germinate, which doesn't always overlap.</p>
        <p><a href="https://extension.illinois.edu/blogs/good-growing/2023-03-31-dilemma-weed-and-feed-lawn-products" target="_blank" rel="noopener noreferrer">Illinois Extension</a> and <a href="https://blogs.ifas.ufl.edu/duvalco/2024/03/08/weed-feed-is-it-helpful-or-hurtful/" target="_blank" rel="noopener noreferrer">UF/IFAS Extension</a> both flag the timing mismatch as the core issue: a spring crabgrass pre-emergent should ideally go down in early March in central Illinois, but the lawn isn't ready for nitrogen until mid-May. In north Florida the gap is even wider (March 1 vs April 15). Putting fertilizer down on dormant turf wastes most of it to runoff and groundwater.</p>
        <p>Weed-and-feed makes the most sense in a narrow window: late spring or early fall, on a lawn with actively growing broadleaf weeds (dandelion, clover) and grass that's pulling nitrogen, applied to a wet canopy so the granules stick. For pre-emergent crabgrass control, separating the two products (a standalone prodiamine pre-emergent in early spring, then fertilizer 4 to 8 weeks later) gives both inputs better timing and usually costs less per square foot.</p>
        <p>If you do use a weed-and-feed: mow 1 to 2 days before, apply to wet leaves so granules adhere, don't water in for 24 to 48 hours (broadleaf herbicide needs leaf contact time), avoid rain for 24 hours, and skip the next mow for 2 to 3 days. Most products take 7 to 14 days to show full weed yellowing and 3 to 4 weeks for complete kill.</p>
      `,
    },
    {
      id: "nutsedge",
      title: "Sedges need their own playbook",
      content: `
        <p>If you sprayed a 3-way and the lighter, glossy, fast-growing spikes are still there, you have nutsedge, not grass. Standard broadleaf herbicides don't touch sedges. The two effective active ingredients for homeowner use are halosulfuron (Sedgehammer, ProSedge) and sulfentrazone (Dismiss).</p>
        <p>Per <a href="https://www.canr.msu.edu/news/controlling_yellow_nutsedge_in_turfgrass" target="_blank" rel="noopener noreferrer">Michigan State Extension</a> and <a href="https://www.extension.purdue.edu/extmedia/TURF-46-W.pdf" target="_blank" rel="noopener noreferrer">Purdue Turfgrass</a>, sulfentrazone shows injury within a few days; halosulfuron takes about 2 weeks but goes deeper into the tubers. Sequential halosulfuron applications 3 weeks apart give greater than 95% control. Treat early summer when the plant is young and actively growing but before tubers form, since established tuber chains can survive a single spray.</p>
        <p>Halosulfuron needs a non-ionic surfactant added to the tank to stick to the waxy sedge leaf. Sulfentrazone does not (the label specifically says don't add surfactant; it can burn turf). Always read the label.</p>
        <p>The long-term fix is drainage. Nutsedge thrives in soggy ground, so a section that grows nutsedge year after year usually has an irrigation leak, a low spot, or compacted soil that won't drain.</p>
      `,
    },
    {
      id: "organic-and-mechanical",
      title: "Organic and mechanical options",
      content: `
        <p>Hand-pulling works for scattered dandelions, plantain, and young crabgrass, especially when the soil is moist after rain or watering. Use a fishtail weeder or dandelion fork to get the full taproot. Pulling adult crabgrass after it has dropped seed in late summer is mostly cosmetic, since the seedbank is already set for next year.</p>
        <p>Corn gluten meal is the organic pre-emergent option. It works as a germination inhibitor at 20 lbs per 1,000 sq ft, but the suppression rate is roughly 50 to 60% in the first year and only climbs after 3 or 4 consecutive years of use. It also adds about 10% nitrogen by weight, which can be a problem if you don't want to fertilize that early. For most homeowners, prodiamine or pendimethalin gives much better first-year control.</p>
        <p>Iron-based broadleaf herbicides (chelated iron HEDTA, sold as Fiesta) burn broadleaf weeds on contact while leaving grass alone. They work on small dandelion and clover and need a follow-up in 10 to 14 days. They're a fit for homeowners avoiding 2,4-D but cost more per application and don't translocate, so they don't kill stubborn perennials.</p>
        <p>Mechanical de-thatching, core aeration, and overseeding bare patches in fall do more for long-term weed pressure than any herbicide. Thick turf shades the soil and prevents weed seeds from getting the light they need to germinate. The single best weed-control investment is a fall overseed.</p>
      `,
    },
    {
      id: "label-and-safety",
      title: "Reading the label and applying safely",
      content: `
        <p>The label is a legal document and "the label is the law" is the EPA's literal phrasing. Three numbers on every herbicide label decide whether you're using it correctly:</p>
        <ul>
          <li><strong>Application rate:</strong> usually expressed as fluid oz per 1,000 sq ft or per gallon of water. Doubling the rate doesn't double the kill, it just doubles the chance of turf damage and groundwater contamination.</li>
          <li><strong>Re-entry interval (REI):</strong> how long before kids and pets can walk on the treated area. For most lawn broadleaf products it's "until the spray has dried" (about 1 to 2 hours).</li>
          <li><strong>Grass tolerance list:</strong> which species the product is labeled safe on. If your grass isn't on the list, don't spray it. St. Augustine and Centipede are missing from a lot of common labels.</li>
        </ul>
        <p>Wear long sleeves, long pants, closed shoes, and chemical-resistant gloves (nitrile, not latex). Mix outdoors in a ventilated spot, never in the kitchen sink. Triple-rinse the empty container and pour the rinse water into the sprayer, not down the drain. The <a href="https://www.epa.gov/earthday/epa-explainshow-read-pesticide-product-label" target="_blank" rel="noopener noreferrer">EPA pesticide label guide</a> walks through the exact label sections.</p>
        <p>Volatility and drift matter most for 2,4-D ester formulations and dicamba. Use the amine formulation when temps are above 80°F to reduce vapor drift onto sensitive plants (tomatoes, grapes, redbuds). Spray with a coarse droplet nozzle and wind under 10 mph.</p>
      `,
    },
  ],
  faqs: [
    {
      question: "When should I apply pre-emergent herbicide?",
      answer:
        "Apply when soil temperature at the upper 1 to 2 inch depth holds at 50 to 55°F for several consecutive days, before crabgrass germinates. The exact date is ZIP-driven: late February in the deep south, mid-March to mid-April in the mid-Atlantic, late April in the upper Midwest. For Poa annua the window flips to late summer or early fall, when soil temps fall through 70°F.",
    },
    {
      question: "What's the difference between pre-emergent and post-emergent herbicides?",
      answer:
        "Pre-emergents stop seeds from germinating but do nothing to weeds already growing. Post-emergents kill growing weeds but do nothing to ungerminated seeds. The wrong choice at the wrong time is the most common reason home weed control fails.",
    },
    {
      question: "Can I spray weed killer on my lawn safely?",
      answer:
        "Selective broadleaf herbicides (2,4-D, dicamba, MCPP combos) are safe on most lawn grasses but St. Augustine and Centipede are sensitive to several common active ingredients. Spray when daytime highs are 60 to 85°F, weeds are actively growing, and there's no rain forecast for 24 hours. Always read the label for grass safety.",
    },
    {
      question: "Should I spot-treat or blanket-spray for weeds?",
      answer:
        "Spot-treat where you can. A pump sprayer with a 3-way handles 90% of homeowner broadleaf problems at a fraction of the cost. Reserve broadcast applications for heavy infestations where more than about 30% of the lawn is affected.",
    },
    {
      question: "When should I apply Scotts Weed and Feed?",
      answer:
        "Late spring or early fall when broadleaf weeds are actively growing and grass is pulling nitrogen. Mow 1 to 2 days before, apply to wet leaves so the granules stick to weed foliage, don't water in for 24 to 48 hours, avoid rain for 24 hours, and skip mowing for 2 to 3 days. Full weed yellowing shows in 7 to 14 days, complete kill in 3 to 4 weeks. For spring crabgrass control, a standalone pre-emergent timed to soil temperature works better than a combo product.",
    },
    {
      question: "How long does weed and feed take to work?",
      answer:
        "Most weed-and-feed products show visible weed yellowing or curling in 7 to 14 days and complete weed kill in 3 to 4 weeks. The fertilizer side starts greening the lawn in 5 to 7 days. If weeds aren't showing any response after 2 weeks, the most common causes are application during dry weather (no leaf moisture for granule adhesion), watering in too soon, or treating weeds that aren't on the product's label.",
    },
    {
      question: "How do I get rid of nutsedge in my lawn?",
      answer:
        "Standard broadleaf herbicides don't kill sedges. Use a sedge-specific product with halosulfuron (Sedgehammer) or sulfentrazone (Dismiss). Halosulfuron needs a non-ionic surfactant added to the tank; sulfentrazone does not. Two applications 3 weeks apart give over 95% control. Treat in early summer when nutsedge is young, before tubers form. Long-term, fix the drainage problem that's letting nutsedge thrive.",
    },
    {
      question: "How long after spraying weed killer can I mow?",
      answer:
        "Wait 2 to 3 days after a systemic broadleaf application before mowing, so the herbicide can translocate from the leaves down to the roots. Mowing too soon removes treated leaf area before the chemical has moved. Conversely, wait 2 to 3 days after mowing before spraying, so weeds have enough leaf area to absorb the herbicide.",
    },
  ],
}

const watering: CareTaskPillar = {
  slug: "watering",
  title: "Lawn Watering: A Complete Guide",
  metaTitle: "Lawn Watering Guide | How Often, How Much, By Grass Type",
  metaDescription:
    "How to water a lawn the right way. Inches per week, deep vs frequent, soil-type adjustments, and a free schedule generator that does the math.",
  heroSubtitle:
    "Deep and infrequent beats shallow and daily. Here is how much water your grass actually needs and how to deliver it.",
  authorSlug: "sarah-mitchell",
  toolSlug: "watering-schedule",
  toolLabel: "Lawn Watering Schedule",
  toolDescription:
    "Enter your grass, soil, and irrigation method and we generate the watering schedule that matches.",
  firstPublished: "2026-05-02",
  lastUpdated: "2026-05-06",
  endCta: {
    title: "Build your lawn's watering schedule",
    description:
      "How much and how often depends on your grass, soil, and sprinklers. Enter the three and get a schedule that matches instead of a generic rule of thumb.",
  },
  keyTakeaways: [
    "Most lawns want 1 to 1.5 inches of water per week, including rainfall.",
    "Deep and infrequent beats shallow and daily; aim for 2 to 3 sessions per week.",
    "Water early morning (4 to 9 am). Evening watering invites disease.",
    "Sandy soil takes more frequent, shorter cycles. Clay takes longer, less-frequent cycles.",
    "Use a tuna can to measure output; you cannot adjust what you don't measure.",
  ],
  howTos: [
    {
      name: "How to water a lawn deeply",
      description: "The repeatable method for delivering a full week's water in a way that drives roots deeper instead of pooling near the surface.",
      totalTime: "PT15M",
      tools: ["Sprinkler", "Empty tuna can or rain gauge"],
      steps: [
        {
          name: "Measure your sprinkler output",
          text: "Place a tuna can in the spray zone and run the sprinkler for 30 minutes. Multiply the inches in the can by 2 to get inches per hour.",
        },
        {
          name: "Calculate runtime",
          text: "Divide your weekly target (typically 1 inch) by the output rate. A sprinkler putting down 0.5 inch per hour needs 2 hours per week.",
        },
        {
          name: "Split into 2 to 3 sessions",
          text: "Break the weekly runtime across 2 to 3 morning sessions, ideally with at least a day between sessions to let the soil breathe.",
        },
        {
          name: "Start before dawn",
          text: "Begin between 4 and 6 am so the lawn dries by mid-morning. This reduces fungal disease pressure significantly.",
        },
        {
          name: "Adjust for soil type",
          text: "Sandy soil drains fast: shorter, more frequent sessions. Clay soil holds water: longer, less frequent sessions with cycle-and-soak if you see runoff.",
        },
      ],
    },
  ],
  sources: [
    {
      publisher: "University of Minnesota Extension",
      label: "Watering Established Lawns",
      url: "https://extension.umn.edu/homes-and-water/watering-lawns-and-gardens",
    },
    {
      publisher: "Texas A&M AgriLife Extension",
      label: "Water-Wise Checklist for Texas Home Lawns",
      url: "https://aggieturf.tamu.edu/wp-content/uploads/sites/24/Water-Wise-Checklist-for-Texas-Home-Lawns-1.pdf",
    },
    {
      publisher: "Colorado State Extension",
      label: "Lawn Care Watering",
      url: "https://extension.colostate.edu/resource/lawn-care-basics/",
    },
  ],
  sections: [
    {
      id: "the-one-inch-rule",
      title: "The one-inch rule, with caveats",
      productPicks: [
        {
          name: "Taylor Freeze-Proof Silicone Rain Gauge",
          description:
            "A cheap way to actually measure the one inch per week this section is built around, instead of guessing at rainfall plus irrigation.",
          category: "Rain gauge",
          affiliateUrl:
            amazonUrl('B0B3267LMT'),
          price: "$18",
          productsPageFilter: "Watering & Irrigation",
          imageUrl: "https://m.media-amazon.com/images/I/31tOeKh8LGL._AC_SL200_.jpg",
        },
      ],
      content: `
        <p>The shorthand most homeowners hear is "one inch of water per week." That's a useful starting point but it leaves out the variables that actually matter: grass type, soil type, irrigation efficiency, root depth, and weather. <a href="https://www.epa.gov/watersense/watering-tips" target="_blank" rel="noopener noreferrer">EPA WaterSense</a> notes landscapes typically want about one inch a week, but the right number swings widely with location and recent weather.</p>
        <p>One inch of water across 1,000 square feet works out to roughly 623 gallons, so the <a href="https://fieldreport.caes.uga.edu/publications/B894/" target="_blank" rel="noopener noreferrer">UGA Extension irrigation guide</a> rounds it to "1 inch equals 600 gallons per 1,000 sqft" as a planning number. A 5,000 sqft lawn is in the 3,000-gallon-per-week range during peak summer.</p>
        <p>The real rule is: water deeply enough to wet the root zone (4 to 6 inches in most lawns, deeper if you've got the soil for it) and infrequently enough that the top inch dries out between waterings. That cycle trains roots to grow down. Daily shallow watering trains them to stay near the surface, where the lawn fries in the first heat wave. UGA's research-backed target is to wet the soil 6 to 8 inches deep on each cycle.</p>
      `,
    },
    {
      id: "evapotranspiration",
      title: "Evapotranspiration: the number that drives everything",
      content: `
        <p>Evapotranspiration (ET) is the combined water loss from soil evaporation and plant transpiration. It is the actual demand your lawn places on water on any given day, and it is what smart controllers and university irrigation guides use instead of a calendar. The <a href="https://edis.ifas.ufl.edu/publication/AE481" target="_blank" rel="noopener noreferrer">UF/IFAS reference ET calculation</a> is the canonical breakdown of the math.</p>
        <p>For practical purposes: cool spring or fall mornings might pull 0.10 inches per day from a lawn. Hot, dry, breezy summer afternoons in Texas, Florida, or the desert Southwest can push past 0.30 inches per day. That is the difference between needing half an inch a week in October and needing 1.5+ inches in July.</p>
        <p>You don't have to compute ET by hand. Local weather services and most state extension turfgrass sites publish daily or weekly ET. If your area runs hot and dry, your lawn will tell you the same thing through wilt before the math catches up.</p>
      `,
    },
    {
      id: "by-soil-type",
      title: "Soil type changes everything",
      content: `
        <p>Sandy soil drains fast and can't hold a deep soak. Schedule shorter, more frequent waterings (roughly half an inch twice a week) so water doesn't run past the roots. Clay soil holds water for days but accepts it slowly. Long single waterings will pool and run off; cycle-and-soak is better (15 minutes on, 30 minutes off, 15 minutes on).</p>
        <p>Loam, the middle ground, accepts most schedules well. The watering schedule generator linked above adjusts the math for whichever soil type you select.</p>
        <p>Soil depth matters as much as texture. A <a href="https://williamson.agrilife.org/2022/06/23/turfgrass-in-drought/" target="_blank" rel="noopener noreferrer">Texas A&amp;M AgriLife drought study</a> found that turfgrass varieties planted over 16 to 18 inches of topsoil all survived 60 days without water, while the same varieties on 4 inches of topsoil did not. If your builder dumped sod over compacted subgrade, no irrigation schedule will fully compensate.</p>
      `,
    },
    {
      id: "catch-can-test",
      title: "Catch-can test: measure your sprinkler output",
      productPicks: [
        {
          name: "Orbit H2O-Six Gear-Drive Sprinkler",
          description:
            "An adjustable-pattern sprinkler to dial in the even coverage you're checking for with the catch-can test above.",
          category: "Sprinkler",
          affiliateUrl:
            amazonUrl('B000FJX4IQ'),
          price: "$24",
          productsPageFilter: "Watering & Irrigation",
          imageUrl: "https://m.media-amazon.com/images/I/21YqxqTLPBL._AC_SL200_.jpg",
        },
      ],
      content: `
        <p>Most homeowners have no idea how much water their sprinkler actually puts down per minute. That is the difference between guessing and dialing it in. The <a href="https://pubs.nmsu.edu/_h/H510/index.html" target="_blank" rel="noopener noreferrer">NMSU catch-can audit guide</a> walks through the procedure and the math.</p>
        <p>Set 5 to 8 empty tuna cans (or straight-sided cups) across one zone, spaced 5 to 8 feet apart for spray heads or 10 to 20 feet apart for rotors. Run the zone for 30 minutes. Measure the water depth in each can with a ruler, average them, and double the result to get inches per hour. If the average is 0.25 inches in 30 minutes, your zone delivers 0.5 inches per hour. To put down half an inch you would run that zone for 30 minutes; to put down an inch, 60 minutes (often split as cycle-and-soak on clay).</p>
        <p>While you're at it, calculate uniformity. Average the lowest 25% of cans, divide by the overall average. Anything under 0.65 means dry corners are dragging the schedule longer than it needs to be. Adjust head spacing or replace clogged nozzles before bumping run times.</p>
      `,
    },
    {
      id: "morning-watering",
      title: "Why morning matters",
      content: `
        <p>Water before 10 a.m. if possible. Two reasons: less evaporation loss (you're delivering more of what you paid for to the roots), and the blades dry out before evening. Evening watering leaves leaves wet overnight, which is how lawn diseases like dollar spot, brown patch, and pythium take hold.</p>
        <p>UGA Extension specifically calls the window between 9 p.m. and 6:30 a.m. the most efficient time, because the dew is already on the canopy and you're not adding hours of leaf wetness. The middle of the day is the worst time: <a href="https://www.epa.gov/watersense/watering-tips" target="_blank" rel="noopener noreferrer">EPA WaterSense</a> notes a large share of midday irrigation is lost to evaporation on hot, breezy days.</p>
        <p>If your only window is evening, water early enough that the canopy dries before sundown.</p>
      `,
    },
    {
      id: "summer-watering",
      title: "How long to water in summer",
      content: `
        <p>Summer is when the question shifts from "should I water" to "how much, how often, how long." A reasonable starting point for most lawns in hot weather: 1 to 1.5 inches per week, split into two or three deep waterings rather than seven shallow ones. Use the catch-can output above to convert inches into minutes for your specific system.</p>
        <p>Texas A&amp;M's <a href="https://agrilifeextension.tamu.edu/library/landscaping/bermudagrass-home-lawn-management-calendar/" target="_blank" rel="noopener noreferrer">Bermudagrass Home Lawn Management Calendar</a> recommends waiting for visible wilt before triggering the next cycle, then watering deeply at night or early morning. That deficit-irrigation approach keeps roots reaching down instead of camping at the surface.</p>
        <p>In 100°F-plus stretches, raise the mowing height a notch first. Taller blades shade soil, lower surface temperature, and cut ET by a measurable amount before you ever change the irrigation timer.</p>
      `,
    },
    {
      id: "drought-stress-signs",
      title: "Reading drought stress (and dormancy vs death)",
      content: `
        <p>Three classic signs your lawn needs water, in order of appearance:</p>
        <ul>
          <li><strong>Color shift.</strong> The blades turn from bright green to a dull blue-gray or smoky green.</li>
          <li><strong>Footprints linger.</strong> Walk across the lawn. Healthy turf bounces back; stressed turf holds the imprint for a minute or longer.</li>
          <li><strong>Folded or rolled blades.</strong> The grass curls inward to reduce leaf surface and slow water loss.</li>
        </ul>
        <p>UGA Extension's threshold for irrigating is when 30% to 50% of the lawn shows those signs. That deficit-irrigation approach saves water without crossing into damage territory.</p>
        <p>If you push past stress and the lawn turns tan or straw-colored, warm-season grasses (Bermuda, Zoysia, Buffalograss, Bahia) usually go into <a href="https://williamson.agrilife.org/2022/06/23/turfgrass-in-drought/" target="_blank" rel="noopener noreferrer">summer dormancy</a> rather than die. The crowns stay alive and green-up returns when water comes back, often within 2 to 3 weeks. Cool-season grasses (Kentucky Bluegrass, Tall Fescue, Fine Fescue) tolerate roughly 4 to 6 weeks of dormancy before crown death starts. If you choose to let a lawn go dormant, give it about half an inch every 2 to 3 weeks to keep crowns hydrated, and stop foot traffic across it.</p>
      `,
    },
    {
      id: "smart-controllers",
      title: "Smart controllers and soil moisture sensors",
      productPicks: [
        {
          name: "Orbit B-hyve Smart Hose Watering Timer",
          description:
            "The hose-tap version of the smart controllers described here: app scheduling plus weather-based skips, without rewiring an in-ground system.",
          category: "Smart timer",
          affiliateUrl:
            amazonUrl('B0758NR8DJ'),
          price: "$79",
          productsPageFilter: "Watering & Irrigation",
          imageUrl: "https://m.media-amazon.com/images/I/41ANo8nPdcL._AC_SL200_.jpg",
        },
      ],
      content: `
        <p>A clock-based controller waters the same minutes whether it rained yesterday or it is 102°F and dry. A WaterSense-labeled <a href="https://www.epa.gov/watersense/weather-based-irrigation-controllers" target="_blank" rel="noopener noreferrer">weather-based irrigation controller</a> pulls local weather and ET to adjust automatically, and EPA estimates households save thousands of gallons per year after the swap.</p>
        <p>Soil moisture sensors are the other lever. They sit in the root zone and tell the controller "skip the cycle, the soil is still wet." The <a href="https://edis.ifas.ufl.edu/publication/ae446" target="_blank" rel="noopener noreferrer">UF/IFAS smart-controller publication</a> documents irrigation reductions of 30% to 70% versus standard timers without quality loss in residential trials.</p>
        <p>If you keep a clock-based controller, at minimum install a working rain sensor (required by code in many states) and reset the schedule each season instead of letting "summer settings" run through October.</p>
      `,
    },
  ],
  faqs: [
    {
      question: "How much water does a lawn need per week?",
      answer:
        "About one inch of water per week (roughly 600 gallons per 1,000 square feet) is the common starting point, but the real rule is to water deeply enough to wet the root zone 6 to 8 inches deep and infrequently enough that the top inch dries between waterings. In peak summer heat that often climbs to 1 to 1.5 inches per week.",
    },
    {
      question: "Is it better to water deeply or daily?",
      answer:
        "Deep and infrequent beats shallow and daily. Daily shallow watering trains roots to stay near the surface, where the lawn fries in the first heat wave. Two or three deep waterings per week is usually a better target than seven shallow ones.",
    },
    {
      question: "What time of day should I water my lawn?",
      answer:
        "Before 10 a.m. is the sweet spot, with the most efficient window running from late evening through early morning when the canopy is already wet from dew. Less evaporation loss, and the blades dry by mid-morning. Wet leaves overnight from a 7 p.m. start are how lawn diseases like dollar spot, brown patch, and pythium take hold.",
    },
    {
      question: "Does soil type change the watering schedule?",
      answer:
        "Yes. Sandy soil drains fast and needs shorter, more frequent waterings (around half an inch twice a week). Clay accepts water slowly and benefits from cycle-and-soak (15 minutes on, 30 minutes off, 15 minutes on). Loam handles most schedules well. Soil depth matters too: turf over 16+ inches of topsoil survives drought far better than turf over 4 inches of soil on compacted subgrade.",
    },
    {
      question: "How long should I run my sprinklers in summer?",
      answer:
        "Run a catch-can test to find out. Set tuna cans across one zone, run for 30 minutes, measure the average depth, and double it to get inches per hour. If the zone puts down 0.5 inches per hour, a 60-minute run delivers an inch. In peak summer most lawns want 1 to 1.5 inches per week split into two or three sessions.",
    },
    {
      question: "What is the best time to water grass in summer?",
      answer:
        "Early morning, ideally between 4 a.m. and 9 a.m. Soil is cool, wind is low, and evaporation losses are at their minimum. Midday watering loses a large share to evaporation on hot, breezy days. Evening watering invites disease by leaving the canopy wet overnight.",
    },
    {
      question: "Will my lawn die if it turns brown in a drought?",
      answer:
        "Usually no. Warm-season grasses (Bermuda, Zoysia, Buffalograss) go into summer dormancy and turn tan or straw-colored to protect their crowns. They typically green back up within 2 to 3 weeks once water returns. Cool-season grasses tolerate roughly 4 to 6 weeks of dormancy before crowns start to die. Keeping crowns alive takes only about half an inch of water every 2 to 3 weeks.",
    },
    {
      question: "Are smart sprinkler controllers worth it?",
      answer:
        "For most in-ground systems, yes. EPA WaterSense-labeled weather-based controllers skip cycles when rain or cool weather have already done the job, and pairing one with a soil moisture sensor can cut irrigation 30% to 70% versus a fixed clock-based timer.",
    },
  ],
}

const mowing: CareTaskPillar = {
  slug: "mowing",
  title: "Lawn Mowing: A Complete Guide",
  metaTitle: "Lawn Mowing Guide | Heights, Frequency, Sharp Blades, By Grass Type",
  metaDescription:
    "How to mow a lawn the right way. Mowing heights by grass type, the one-third rule, blade sharpening, and a calculator that gives you exact heights.",
  heroSubtitle:
    "Cutting height is the single biggest variable in how a lawn looks. Get it right for your grass and most other problems get easier.",
  authorSlug: "james-thornton",
  toolSlug: "mowing-calculator",
  toolLabel: "Mowing Height Calculator",
  toolDescription:
    "Tell us your grass type and season; we tell you the height to set and how often to cut.",
  firstPublished: "2026-05-02",
  lastUpdated: "2026-05-06",
  endCta: {
    title: "Dial in your mowing height",
    description:
      "The right height changes with your grass and the season. Tell us both and get the exact deck setting and how often to cut.",
  },
  keyTakeaways: [
    "Cutting height is the single biggest variable in how a lawn looks.",
    "Never remove more than one-third of the blade in a single mowing.",
    "Cool-season grasses go 2.5 to 4 inches; warm-season 1 to 3 inches depending on species.",
    "Sharp blades cut. Dull blades tear. Sharpen at least once per season.",
    "Mulch clippings back into the lawn; they return nitrogen and do not cause thatch.",
  ],
  howTos: [
    {
      name: "How to mow a lawn correctly",
      description: "The repeatable method for mowing that produces a clean cut, even appearance, and minimal stress on the grass.",
      totalTime: "PT45M",
      tools: ["Mower with a sharp blade", "Lawn edger or string trimmer"],
      steps: [
        {
          name: "Pick the right height for your grass",
          text: "Set mower deck to your grass type's recommended height: Bermuda 1 to 1.5 inches, KBG 2.5 to 3.5 inches, Tall Fescue 3 to 4 inches, St. Augustine 3 to 4 inches.",
        },
        {
          name: "Mow dry grass",
          text: "Wet grass clumps in the deck, leaves streaks, and stresses the blade. Wait until morning dew has dried.",
        },
        {
          name: "Alternate the pattern",
          text: "Mow north-south one week, east-west the next. Alternating directions keeps the grass from leaning and helps the lawn stand up.",
        },
        {
          name: "Watch the one-third rule",
          text: "Never remove more than one third of the blade in a single mowing. If the lawn got tall, take it down over two cuttings spaced 2 to 3 days apart.",
        },
        {
          name: "Mulch the clippings",
          text: "Leave clippings on the lawn. They decompose in days and return roughly 25 percent of the lawn's annual nitrogen need.",
        },
      ],
    },
  ],
  sources: [
    {
      publisher: "NC State Extension",
      label: "Mowing Your Lawn: The One-Third Rule",
      url: "https://content.ces.ncsu.edu/catalog/series/227/",
    },
    {
      publisher: "Penn State Extension",
      label: "Lawn Mowing Tips",
      url: "https://extension.psu.edu/trees-lawns-and-landscaping/turfgrass-and-lawn-care/establishment-and-renovation",
    },
    {
      publisher: "University of Minnesota Extension",
      label: "Mowing Practices for Healthy Lawns",
      url: "https://extension.umn.edu/lawncare/mowing-practices-healthy-lawns",
    },
  ],
  sections: [
    {
      id: "one-third-rule",
      title: "The one-third rule",
      content: `
        <p>Never cut more than one third of the leaf blade in a single mowing. If your target height is 3 inches, mow when the grass hits 4.5. Cut more than that and you scalp the plant, a stress event that triggers thinning, weed encroachment, and slow recovery. This is the single most cited mowing principle in turfgrass research, and every land-grant extension service teaches it the same way (see the <a href="https://content.ces.ncsu.edu/extension-gardener-handbook/9-lawns" target="_blank" rel="noopener noreferrer">NCSU Extension Gardener Handbook chapter on lawns</a>).</p>
        <p>Why one third? When you remove more leaf tissue than that, the plant stops sending energy to roots and pours it into regrowing the shoot. Root depth flattens, drought tolerance drops, and recovery from any other stressor (heat, foot traffic, disease) slows down for weeks.</p>
        <p>Irregular mowing schedules cause more damage than people realize. A two-week gap during peak spring growth often forces a scalping cut just to get back to height. If you fall behind, raise the deck for the first pass, drop it the next mow, and ease back to your normal height over two or three cuts instead of one.</p>
      `,
    },
    {
      id: "height-by-species",
      title: "Right height for the right grass",
      content: `
        <p>Mowing height is grass-dependent, not preference-dependent. Cut a Kentucky Bluegrass lawn at 1 inch and it thins out. Let St. Augustine grow to 5 inches and it falls over and shades itself out. Use the species ranges below; they line up with the recommendations from the <a href="https://extension.uga.edu/content/dam/extension-county-offices/dougherty-county/anr/Mowing-heights.pdf" target="_blank" rel="noopener noreferrer">UGA Master Gardener mowing-height table</a> and the <a href="https://agrilifeextension.tamu.edu/wp-content/uploads/2025/06/Turf-Cutting-height.pdf" target="_blank" rel="noopener noreferrer">Texas A&M AgriLife warm-season mowing chart</a>.</p>
        <h3>Cool-season grasses</h3>
        <ul>
          <li><strong>Tall Fescue:</strong> 3 to 4 inches. Most home lawns thrive at 3.5.</li>
          <li><strong>Kentucky Bluegrass:</strong> 2 to 3 inches solo, 2.5 to 3.5 in a blend.</li>
          <li><strong>Fine Fescue:</strong> 2 to 3 inches; happy at 3+ in shade.</li>
          <li><strong>Perennial Ryegrass:</strong> 1.5 to 2.5 inches in a blend with KBG.</li>
        </ul>
        <h3>Warm-season grasses</h3>
        <ul>
          <li><strong>Common Bermuda:</strong> 1 to 2 inches with a rotary mower.</li>
          <li><strong>Hybrid Bermuda (Tifway, Latitude 36):</strong> 0.5 to 1 inch with a reel mower for tournament-grade turf, or 1 to 1.5 inches with a sharp rotary.</li>
          <li><strong>Zoysia:</strong> 1 to 2.5 inches depending on cultivar; Emerald and Zeon take the lower end, Meyer the upper.</li>
          <li><strong>St. Augustine:</strong> 3.5 to 4 inches. Cutting below 3 invites brown-patch and weed pressure.</li>
          <li><strong>Centipede:</strong> 1 to 2 inches. Mow when it reaches 1.5 to stay inside the one-third rule.</li>
          <li><strong>Bahia:</strong> 3 to 4 inches; expect coarse seedheads even with frequent mowing.</li>
        </ul>
        <p>Within a season, raise the deck a half-inch during summer heat or drought. Taller blades shade the soil, slow evaporation, and protect crowns. Drop back down by 0.25 inch increments as conditions ease.</p>
      `,
    },
    {
      id: "best-time-to-mow",
      title: "Best time of day to mow",
      content: `
        <p>Mid-morning to early evening, after the dew has burned off and before peak heat, is the best window. Wet grass clumps under the deck, plugs the discharge chute, and leaves a ragged cut that browns at the tip. Mowing at noon in 95-degree heat compounds drought stress and bruises freshly cut leaf edges.</p>
        <p>Practical target: between 9 and 11 a.m., or after 4 p.m. once the heat backs off. Avoid mowing within 24 hours of a fungicide or herbicide application so the product has time to absorb.</p>
      `,
    },
    {
      id: "sharp-blades",
      title: "Sharp blades change the lawn",
      productPicks: [
        {
          name: "Sharp Pebble Cordless Lawn Mower Blade Sharpener",
          description:
            "A home sharpener so you can keep the clean cut this section is about, instead of tearing the grass tips with a dull blade between shop trips.",
          category: "Blade sharpener",
          affiliateUrl:
            amazonUrl('B0DFFPDYL8'),
          price: "$60",
          productsPageFilter: "Lawn Mowers & Maintenance Tools",
          imageUrl: "https://m.media-amazon.com/images/I/51wtSJuRSTL._AC_SL200_.jpg",
        },
      ],
      content: `
        <p>A dull blade tears grass tips instead of slicing them. Within 24 hours those torn tips brown out, and from 30 feet the lawn looks like it has a gray haze across the top. Torn leaf edges also stay open longer than a clean cut, which is an open door for fungal spores. The <a href="https://extension.umn.edu/lawn-care/mowing-practices-healthy-lawns" target="_blank" rel="noopener noreferrer">University of Minnesota Extension</a> lists dull blades as a top contributor to lawn disease pressure.</p>
        <h3>Sharpening cadence</h3>
        <ul>
          <li><strong>Residential rotary:</strong> sharpen every 20 to 25 hours of run time, or twice a season for a typical 6,000 to 10,000 sq ft lawn.</li>
          <li><strong>Heavy use or sandy soil:</strong> every 8 to 10 hours; sand dulls steel fast.</li>
          <li><strong>Reel mower:</strong> backlap once a season; full grind every 2 to 3 years.</li>
        </ul>
        <p>Tip-speed at the cutting edge of a 21-inch rotary blade runs around 17,000 to 19,000 feet per minute, fast enough to slice cleanly only if the edge is honed to a consistent 30-degree bevel. A nicked, rounded edge bludgeons rather than cuts. Keep a backup blade swapped in while the dull one is on the bench.</p>
      `,
    },
    {
      id: "mulch-bag-discharge",
      title: "Mulching, bagging, or side discharge",
      content: `
        <p>Default to mulching. The clippings break down within a week or two and return nitrogen, potassium, and micronutrients to the soil. The <a href="https://extension.umn.edu/lawn-care/what-do-lawn-clippings" target="_blank" rel="noopener noreferrer">University of Minnesota Extension grass-clippings guide</a> notes that recycled clippings add the equivalent of one fertilizer application per year and do not contribute to thatch (a long-running myth).</p>
        <p><strong>Bag</strong> when the lawn is actively diseased (leaf spot, dollar spot, rust, brown patch), when seedheads are heavy in spring, or when you let the lawn get too tall and clippings would mat together and smother the canopy.</p>
        <p><strong>Side discharge</strong> works for tall, dry grass when you want to spread clippings thin and walk back over them with a second pass. It is the cleanest option for the first cut of spring on a lawn that grew too long over winter dormancy.</p>
      `,
    },
    {
      id: "reel-vs-rotary",
      title: "Reel vs rotary vs robotic",
      productPicks: [
        {
          name: "American Lawn Mower Company 14-Inch Reel Mower",
          description:
            "A classic push reel mower for the clean scissor cut described here, ideal for the low, well-groomed heights that suit reel mowing.",
          category: "Reel mower",
          affiliateUrl:
            amazonUrl('B00004RA3F'),
          price: "$88",
          productsPageFilter: "Lawn Mowers & Maintenance Tools",
          imageUrl: "https://m.media-amazon.com/images/I/31f0r+Tws+L._AC_SL200_.jpg",
        },
      ],
      content: `
        <p>The mower type matters more on warm-season lawns than cool-season ones. A rotary blade impacts the leaf with brute speed; a reel cuts with a scissoring action that leaves a much cleaner edge.</p>
        <ul>
          <li><strong>Rotary (gas or battery):</strong> the right tool for cool-season lawns and any cut above 1.5 inches. Versatile, handles uneven terrain, easy to maintain.</li>
          <li><strong>Reel:</strong> the right tool for hybrid Bermuda or Zoysia kept under 1 inch. A rotary cannot maintain a clean cut at that height; the deck scalps high spots and skips low ones.</li>
          <li><strong>Robotic:</strong> mows daily or every other day, removes only a sliver each pass, and inherently respects the one-third rule. Best for flat, fenced lawns with consistent turf. Cuts so little leaf tissue per pass that clippings disappear into the canopy.</li>
        </ul>
        <p>Battery mowers have closed most of the gap with gas on small to mid-size lawns, with the bonus of quieter operation and zero exhaust. Gas still wins for lawns over half an acre or for thick, wet spring growth where torque matters.</p>
      `,
    },
    {
      id: "mowing-patterns-striping",
      title: "Mowing patterns and striping",
      content: `
        <p>Alternate your mowing direction every cut. Always going north-south compacts the soil along the same wheel tracks and trains the leaf blades to lay over in one direction, which dulls the visual contrast. Cycle through north-south, east-west, and the two diagonals over four mows.</p>
        <p>Striping is leaf-blade orientation reflecting light. Push a roller-equipped mower (or a striping kit clamped behind the deck) in straight lines and the bent-toward-you blades look dark while the bent-away blades look light. Cool-season grasses (especially Kentucky Bluegrass) stripe far better than warm-season because their leaf blades are softer and bend more readily. Bermuda and Zoysia stripe weakly even with a roller.</p>
      `,
    },
    {
      id: "spring-scalping",
      title: "Spring scalping for warm-season lawns",
      content: `
        <p>Once a year, in early spring, warm-season lawns benefit from a deliberate low cut to remove last season's dormant top growth. Drop the deck a notch or two below your normal height, bag the clippings, and let sunlight hit the soil to warm it and trigger green-up. Time it after the last hard frost but before active growth resumes. This is standard practice for Bermuda and Zoysia. It is risky on St. Augustine and not recommended for cool-season lawns.</p>
        <p>Do not attempt a spring scalp once the grass has greened up; you will cut into living tissue and set the lawn back by weeks.</p>
      `,
    },
  ],
  faqs: [
    {
      question: "How short should I cut my grass?",
      answer:
        "Most cool-season grasses look and grow best at 3 to 4 inches. Common Bermuda runs 1 to 2 inches; hybrid Bermuda goes lower with a reel mower. Zoysia is comfortable at 1.5 to 2.5. St. Augustine wants 3.5 to 4 inches. Centipede sits at 1 to 2 inches.",
    },
    {
      question: "What is the one-third rule for mowing?",
      answer:
        "Never cut more than one third of the leaf blade in a single mowing. If your target height is 3 inches, mow when the grass hits 4.5. Cutting more than that scalps the plant and triggers thinning, weed encroachment, and slow recovery.",
    },
    {
      question: "How often should I sharpen my mower blade?",
      answer:
        "Sharpen or replace mower blades every 20 to 25 hours of running time, or twice a season for a typical residential lawn. Sandy soils and large lawns push that closer to every 8 to 10 hours. A dull blade tears grass tips, browns the leaf within a day, and opens the plant to disease.",
    },
    {
      question: "Should I bag or mulch grass clippings?",
      answer:
        "Mulch by default. Returning clippings adds the equivalent of one fertilizer application per year and does not cause thatch. Bag when the lawn is actively diseased, when seedheads are heavy in spring, or after a missed mow that left the grass too tall.",
    },
    {
      question: "Should I raise the mowing height in summer?",
      answer:
        "Yes, especially during heat or drought. Raise the deck a half-inch through the hottest stretch of summer. Taller blades shade the soil, slow evaporation, and protect the crowns. Drop back down by 0.25 inch increments as conditions ease.",
    },
    {
      question: "What is the best time of day to mow the lawn?",
      answer:
        "Mid-morning to early evening, after dew has dried and before peak heat. Roughly 9 to 11 a.m. or after 4 p.m. is ideal. Wet grass clumps and tears, and noon mowing in 95-degree heat compounds drought stress on freshly cut leaf edges.",
    },
    {
      question: "Can I mow wet grass?",
      answer:
        "Avoid it when possible. Wet blades fold under the deck instead of standing up to the cut, leaving a ragged edge that browns and invites disease. Wet clippings also clump, plug the discharge, and mat together on the canopy. If you have to mow wet, raise the deck, slow your pace, and side-discharge instead of bagging.",
    },
    {
      question: "What is the best mower for St. Augustine grass?",
      answer:
        "A sharp rotary mower with a high deck setting (3.5 to 4 inches) is the right tool. St. Augustine has wide, succulent leaf blades that bruise easily, so blade sharpness matters more than mower brand. Reel mowers are not recommended; St. Augustine is too tall and too coarse for a clean reel cut.",
    },
    {
      question: "Should I scalp my Bermuda lawn in spring?",
      answer:
        "Yes, a single deliberate scalp once a year is standard practice for Bermuda and Zoysia. Drop the deck a notch or two below your normal height, bag the clippings, and time it after the last hard frost but before active green-up. Do not scalp once the grass has greened up, and do not scalp St. Augustine or cool-season lawns.",
    },
  ],
}

export const CARE_TASK_PILLARS: Record<string, CareTaskPillar> = {
  fertilizing,
  "weed-control": weedControl,
  watering,
  mowing,
}

export function getCareTaskPillar(slug: string): CareTaskPillar | undefined {
  return CARE_TASK_PILLARS[slug]
}

export function getAllCareTaskSlugs(): string[] {
  return Object.keys(CARE_TASK_PILLARS)
}

export function isCareTaskSlug(slug: string): boolean {
  return slug in CARE_TASK_PILLARS
}
